import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";

import { Option } from "../types";
import { useLocaleProvider } from "../../../components/locale-provider";
import Selector from "../../../components/selector";
import { useUserPropertyTickers } from "../../../components/user-tickers-providers";
import {
  StyledGrid,
  StyledRow,
  StyledCol,
  StyledContainerInputIcon,
  StyledInputIcon,
} from "../styled";
import { BasicInput, BasicSelect } from "./Inputs";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { addDays, format, isAfter, isSameDay } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const messages = defineMessages({
  formQuotationDataEstablishment: {
    id: "quotation.formQuotationData.establishment",
    defaultMessage: "Establecimiento",
  },
  formQuotationDataCountry: {
    id: "quotation.formQuotationData.country",
    defaultMessage: "País",
  },
  formQuotationDataCheckin: {
    id: "quotation.formQuotationData.checkin",
    defaultMessage: "Entrada",
  },
  formQuotationDataLang: {
    id: "quotation.formQuotationData.lang",
    defaultMessage: "Idioma",
  },
  formQuotationDataCheckout: {
    id: "quotation.formQuotationData.checkout",
    defaultMessage: "Salida",
  },
  formQuotationDataCurrency: {
    id: "quotation.formQuotationData.currency",
    defaultMessage: "Moneda",
  },
  formQuotationDataDevice: {
    id: "quotation.formQuotationData.device",
    defaultMessage: "Dispositivo",
  },
  formQuotationDataCondition: {
    id: "quotation.formQuotationData.condition",
    defaultMessage: "Condición",
  },
  formQuotationDataPromocode: {
    id: "quotation.formQuotationData.promocode",
    defaultMessage: "Promocode",
  },
  formQuotationDataValidPromocode: {
    id: "quotation.formQuotationData.validpromocode",
    defaultMessage: "promocode valido",
  },
  formQuotationDataInvalidPromocode: {
    id: "quotation.formQuotationData.invalidpromocode",
    defaultMessage: "promocode invalidos: {promoCodes}",
  },
  formQuotationDataAgent: {
    id: "quotation.formQuotationData.agent",
    defaultMessage: "Agente",
  },
  formQuotationDataSelectCountry: {
    id: "quotation.formQuotationData.selectCountry",
    defaultMessage: "Seleccionar País",
  },
  formQuotationDataPlaceholderEmail: {
    id: "quotation.formQuotationData.placeholderEmail",
    defaultMessage: "Email cliente",
  },
  formQuotationDataInsertPromocode: {
    id: "quotation.formQuotationData.insertPromocode",
    defaultMessage: "Ingrese un promocode",
  },
  formQuotationDataComment: {
    id: "quotation.formQuotationData.comment",
    defaultMessage: "Comentario",
  },
  tooltipPromo: {
    id: "quotation.formQuotationData.tooltipPromo",
    defaultMessage: "tooltipPromo",
  },
});

export const FormQuotationData = ({
  handleConfirmChangeEstablishment,
  tab,
  changeValue,
  changeConditions,
  validatePromoCode,
  listOfDevice,
}: any) => {
  const intl = useIntl();
  const userPropertyTickersList = useUserPropertyTickers();
  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);

  const hasInvalidPromoCodes = useCallback((tab: any) => {
    return tab?.invalidPromoCodes && tab.invalidPromoCodes.length > 0;
  }, []);

  const hasValidPromoCodes = useCallback((tab: any) => {
    return tab?.validPromoCode > 0;
  }, []);

  useEffect(() => {
    const fetchPropertyTickers = async () => {
      const newDataList = userPropertyTickersList.map(option => ({ value: option, label: option }));
      setPropertyTickerOptions(newDataList);
    };
    fetchPropertyTickers();
  }, [userPropertyTickersList]);

  useEffect(
    function resetCheckOutIfCheckInSameOrAfter() {
      if (
        isAfter(new Date(tab.checkIn), new Date(tab.checkOut)) ||
        isSameDay(new Date(tab.checkIn), new Date(tab.checkOut))
      ) {
        changeValue("checkOut", "");
      }
    },
    [changeValue, tab.checkIn, tab.checkOut]
  );

  const tooltipConfig = useMemo(
    () => ({
      delay: { show: 250, hide: 0 },
    }),
    []
  );

  const tooltipPromo = useMemo(
    () => (
      <Tooltip id="tooltipPromo">
        <FormattedMessage {...messages.tooltipPromo} />
      </Tooltip>
    ),
    []
  );

  const conditionsOptions = useMemo(
    () =>
      tab?.conditions?.map((condition: any) => {
        condition.label = condition.translations.find(
          (it: any) => it.locale === tab.lang.value
        )?.name;
        return condition;
      }),
    [tab.conditions, tab.lang]
  );

  return (
    <StyledGrid padding={"20px"}>
      <StyledRow paddingTop={"20px"}>
        <StyledCol xs={{ span: 3 }}>
          <span style={{ width: "40%" }}>
            <FormattedMessage {...messages.formQuotationDataEstablishment} />
          </span>
          <div style={{ display: "block", width: "100%" }}>
            <Selector
              options={propertyTickerOptions}
              value={tab.establishment}
              onChange={async (selectedOption: any) => {
                handleConfirmChangeEstablishment(selectedOption);
              }}
              placeholder={"-"}
            />
          </div>
        </StyledCol>
        <StyledCol xs={{ span: 3, offset: 1 }}>
          <BasicInput
            id={intl.formatMessage(messages.formQuotationDataAgent)}
            label={intl.formatMessage(messages.formQuotationDataAgent)}
            value={tab.agent}
            disabled
          />
        </StyledCol>
        <StyledCol xs={{ span: 3, offset: 1 }}>
          <span style={{ width: "40%" }}>
            <FormattedMessage {...messages.formQuotationDataLang} />
          </span>
          <div style={{ display: "block", width: "100%" }}>
            <Selector
              options={tab?.configurations?.availableLocales || []}
              value={tab.lang}
              onChange={(selectedOption: any) => changeValue("lang", selectedOption)}
              placeholder={"-"}
            />
          </div>
        </StyledCol>
      </StyledRow>

      <StyledRow paddingTop={"20px"}>
        <StyledCol xs={{ span: 3 }}>
          <span style={{ width: "40%" }}>
            <FormattedMessage {...messages.formQuotationDataCheckin} />
          </span>
          <BasicInput
            style={{ width: "100%" }}
            type="date"
            id="checkIn"
            name="checkIn"
            value={tab.checkIn}
            onChange={(event: any) => {
              changeValue("checkIn", event);
            }}
          />
        </StyledCol>
        <StyledCol xs={{ span: 3, offset: 1 }}>
          <BasicInput
            id={"ID Cliente"}
            label={"ID Cliente"}
            value={tab.idClient}
            onChange={(value: string) => changeValue("idClient", value)}
            placeholder={intl.formatMessage(messages.formQuotationDataPlaceholderEmail)}
          />
        </StyledCol>
        <StyledCol xs={{ span: 3, offset: 1 }}>
          <BasicInput
            id={"Comentario"}
            label={intl.formatMessage(messages.formQuotationDataComment)}
            value={tab.comment}
            onChange={(value: string) => changeValue("comment", value)}
          />
        </StyledCol>
      </StyledRow>

      <StyledRow paddingTop={"20px"}>
        <StyledCol xs={{ span: 3 }}>
          <span style={{ width: "40%" }}>
            <FormattedMessage {...messages.formQuotationDataCheckout} />
          </span>
          <BasicInput
            style={{ width: "100%" }}
            type="date"
            id="checkOut"
            name="checkOut"
            value={tab.checkOut}
            onChange={(event: any) => {
              changeValue("checkOut", event);
            }}
            minDate={format(
              addDays(tab.checkIn ? new Date(tab.checkIn) : new Date(), 1),
              "yyyy-MM-dd"
            )}
          />
        </StyledCol>

        <StyledCol xs={{ span: 3, offset: 1 }}>
          <span style={{ width: "40%" }}>
            <FormattedMessage {...messages.formQuotationDataCurrency} />
          </span>
          <div style={{ display: "block", width: "100%" }}>
            <BasicSelect
              onChange={(value: any) => {
                changeValue("currency", value);
              }}
              title={tab.currency.value}
              options={tab?.configurations?.availableCurrencies || []}
            />
          </div>
        </StyledCol>
      </StyledRow>

      <StyledRow paddingTop={"20px"}>
        <StyledCol xs={{ span: 3 }}>
          <span style={{ width: "40%" }}>
            <FormattedMessage {...messages.formQuotationDataCondition} />
          </span>
          <div style={{ display: "block", width: "100%" }}>
            <Selector
              options={conditionsOptions || []}
              value={tab.condition}
              onChange={(selectedOption: any) => changeConditions(selectedOption)}
              placeholder={"-"}
              ignoreCustomComponents={true}
            />
          </div>
        </StyledCol>
        <StyledCol xs={{ span: 3, offset: 1 }}>
          <span style={{ width: "40%" }}>
            <OverlayTrigger {...tooltipConfig} overlay={tooltipPromo}>
              <FontAwesomeIcon icon={faInfoCircle} color="#B3B3B3" style={{ marginRight: 5 }} />
            </OverlayTrigger>
            <FormattedMessage {...messages.formQuotationDataPromocode} />
          </span>
          <div style={{ display: "block", width: "100%" }}>
            <StyledContainerInputIcon>
              <StyledInputIcon
                type={"text"}
                onChange={(e: any) => changeValue("promoCodeTemp", e.target.value)}
                value={tab?.promoCodeTemp || ""}
                placeholder={intl.formatMessage(messages.formQuotationDataInsertPromocode)}
              />
              <svg
                onClick={validatePromoCode}
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6.75 0C3.0375 0 0 3.0375 0 6.75C0 10.4625 3.0375 13.5 6.75 13.5C8.60625 13.5 10.3275 12.7744 11.5425 11.5425L10.3275 10.3275C9.41625 11.2387 8.15063 11.8125 6.73313 11.8125C3.93187 11.8125 1.67062 9.55125 1.67062 6.75C1.67062 3.94875 3.93187 1.6875 6.73313 1.6875C8.13375 1.6875 9.34875 2.295 10.26 3.22312L8.42062 5.0625H13.4831V0L11.475 2.00813C10.26 0.793125 8.58938 0 6.73313 0H6.75Z"
                  fill="#656565"
                />
              </svg>
            </StyledContainerInputIcon>
            {!hasInvalidPromoCodes(tab) && hasValidPromoCodes(tab) && (
              <Form.Text id="passwordHelpBlock" muted>
                <FormattedMessage {...messages.formQuotationDataValidPromocode} />
              </Form.Text>
            )}
            {hasInvalidPromoCodes(tab) && (
              <Form.Text id="passwordHelpBlock" muted>
                <FormattedMessage {...messages.formQuotationDataInvalidPromocode} values={{ promoCode: tab?.invalidPromoCodes.join(",")}} />
              </Form.Text>
            )}
          </div>
        </StyledCol>
      </StyledRow>
    </StyledGrid>
  );
};
